import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import * as NotificationsActions from '../notifications/notifications.actions';
import { Notification, NotificationLevel } from 'src/app/model/notification';
import { Store } from '@ngrx/store';
import { AppState } from '../app.reducer';
import { TranslateService } from '@ngx-translate/core';
import * as DeviceVctActions from './device-vct.actions';
import { DeviceVctService } from 'src/app/services/device-vct.service';


@Injectable()
export class DeviceVctEffects {

    getSitesVctList$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceVctActions.getSitesVctList),
        switchMap(() => this.deviceVctService.getSitesVctList()
            .pipe(
                map(resp => {
                    if (resp !== undefined) {
                        return DeviceVctActions.getSitesVctListSuccess({ sitesList: resp });
                    }
                    return DeviceVctActions.getSitesVctListListFailed({ errorMessage: this.translate.instant('ERROR') });
                }),
                catchError(error => of(DeviceVctActions.getSitesVctListListFailed({ errorMessage: error.message })))
            )
        )
    ));

    getDevicesVctList$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceVctActions.getDevicesVctList),
        withLatestFrom(
            this.store.select('site', 'currentSite'),
        ),
        switchMap(([, site]) => this.deviceVctService.getDevicesVctList(site.Id)
            .pipe(
                map(resp => {
                    if (resp !== undefined) {
                        return DeviceVctActions.getDevicesVctListSuccess({ plantsList: resp });
                    }
                    return DeviceVctActions.getDevicesVctListFailed({ errorMessage: this.translate.instant('ERROR') });
                }),
                catchError(error => of(DeviceVctActions.getDevicesVctListFailed({ errorMessage: error.message })))
            )
        )
    ));


    getVctInvitations$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceVctActions.getVctInvitations),
        switchMap(() => this.deviceVctService.getVctInvitations()
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceVctActions.getVctInvitationsSuccess({ invitationsList: resp.Data });
                    } else {
                        return DeviceVctActions.getVctInvitationsFailed({ errorMessage: this.translate.instant('ERROR') });
                    }

                }),
                catchError(error => of(DeviceVctActions.getVctInvitationsFailed({ errorMessage: error.message })))
            )
        )
    ));


    createVctInvitations$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceVctActions.createVctInvitations),
        switchMap((action) => this.deviceVctService.createVctInvitations(action.invitation, action.cleanup, action.accept)
            .pipe(
                map(resp => {
                    if (resp !== undefined) {
                        return DeviceVctActions.createVctInvitationsSuccess({ message: this.translate.instant('VCT_INVITATION_CREATE_SUCCESS') });
                    }
                    return DeviceVctActions.createVctInvitationsFailed({ errorMessage: this.translate.instant('ERROR') });
                }),
                catchError(error => of(DeviceVctActions.createVctInvitationsFailed({ errorMessage: error.message })))
            )
        )
    ));

    removeVctInvitations$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceVctActions.removeVctInvitations),
        switchMap((action) => this.deviceVctService.removeVctInvitations(action.invitationId)
            .pipe(
                map(resp => {
                    if (resp !== undefined) {
                        return DeviceVctActions.removeVctInvitationsSuccess({ message: this.translate.instant('VCT_INVITATION_REMOVE_SUCCESS') });
                    }
                    return DeviceVctActions.removeVctInvitationsFailed({ errorMessage: this.translate.instant('ERROR') });
                }),
                catchError(error => of(DeviceVctActions.removeVctInvitationsFailed({ errorMessage: error.message })))
            )
        )
    ));


    editVctModuleName$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceVctActions.editVctModuleName),
        switchMap((action) => this.deviceVctService.editVctModuleName(action.moduleId, action.aliasName)
            .pipe(
                map(resp => {
                    if (resp !== undefined) {
                        return DeviceVctActions.editVctModuleNameSuccess({ message: this.translate.instant('VCT_INVITATION_CREATE_SUCCESS') });
                    }
                    return DeviceVctActions.editVctModuleNameFailed({ errorMessage: this.translate.instant('ERROR') });
                }),
                catchError(error => of(DeviceVctActions.editVctModuleNameFailed({ errorMessage: error.message })))
            )
        )
    ));


    editVctFeatureName$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceVctActions.editVctFeatureName),
        switchMap((action) => this.deviceVctService.editVctFeatureName(action.featureId, action.aliasName)
            .pipe(
                map(resp => {
                    if (resp !== undefined) {
                        return DeviceVctActions.editVctFeatureNameSuccess({ message: this.translate.instant('VCT_MODIFY_SUCCESS') });
                    }
                    return DeviceVctActions.editVctFeatureNameFailed({ errorMessage: this.translate.instant('ERROR') });
                }),
                catchError(error => of(DeviceVctActions.editVctFeatureNameFailed({ errorMessage: error.message })))
            )
        )
    ));



    enableVctAux$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceVctActions.enableVctAux),
        switchMap((action) => this.deviceVctService.enableVctAux(action.moduleId, action.auxId)
            .pipe(
                map(resp => {
                    if (resp !== undefined) {
                        return DeviceVctActions.enableVctAuxSuccess({ message: this.translate.instant('VCT_ENABLE_AUX') });
                    }
                    return DeviceVctActions.enableVctAuxFailed({ errorMessage: this.translate.instant('ERROR') });
                }),
                catchError(error => of(DeviceVctActions.enableVctAuxFailed({ errorMessage: error.message })))
            )
        )
    ));


    editVctAux$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceVctActions.editVctAux),
        switchMap((action) => this.deviceVctService.editVctAux(action.moduleId, action.auxId, action.icon)
            .pipe(
                map(resp => {
                    if (resp !== undefined) {
                        return DeviceVctActions.editVctAuxSuccess({ message: this.translate.instant('VCT_EDIT_AUX') });
                    }
                    return DeviceVctActions.editVctAuxFailed({ errorMessage: this.translate.instant('ERROR') });
                }),
                catchError(error => of(DeviceVctActions.editVctAuxFailed({ errorMessage: error.message })))
            )
        )
    ));

    resetVctInvitationApp$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceVctActions.resetVctInvitationApp),
        switchMap((action) => this.deviceVctService.resetVctInvitationApp(action.featureId)
            .pipe(
                map(resp => {
                    if (resp !== undefined) {
                        return DeviceVctActions.resetVctInvitationAppSuccess({ message: this.translate.instant('RESET_VCT_INVITATION_APP_SUCCESS') });
                    }
                    return DeviceVctActions.resetVctInvitationAppFailed({ errorMessage: this.translate.instant('RESET_VCT_INVITATION_APP_ERROR') });
                }),
                catchError(error => of(DeviceVctActions.resetVctInvitationAppFailed({ errorMessage: error.message })))
            )
        )
    ));


    disableVctAux$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceVctActions.disableVctAux),
        switchMap((action) => this.deviceVctService.disableVctAux(action.moduleId, action.auxId)
            .pipe(
                map(resp => {
                    if (resp !== undefined) {
                        return DeviceVctActions.disableVctAuxSuccess({ message: this.translate.instant('VCT_DISABLE_AUX') });
                    }
                    return DeviceVctActions.disableVctAuxFailed({ errorMessage: this.translate.instant('ERROR') });
                }),
                catchError(error => of(DeviceVctActions.disableVctAuxFailed({ errorMessage: error.message })))
            )
        )
    ));

    abandonVctPlant$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceVctActions.abandonVctPlant),
        switchMap((action) => this.deviceVctService.abandonVctPlant(action.plantId, action.cleanup)
            .pipe(
                map(resp => {
                    if (resp !== undefined) {
                        return DeviceVctActions.abandonVctPlantSuccess({ message: this.translate.instant('VCT_ABANDON_PLANT_SUCCESS') });
                    }
                    return DeviceVctActions.abandonVctPlantFailed({ errorMessage: this.translate.instant('ERROR') });
                }),
                catchError(error => of(DeviceVctActions.abandonVctPlantFailed({ errorMessage: error.message })))
            )
        )
    ));

    abandonVctModule$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceVctActions.abandonVctModule),
        switchMap((action) => this.deviceVctService.abandonVctModule(action.moduleId)
            .pipe(
                map(resp => {
                    if (resp !== undefined) {
                        if (action.abandon) {
                            return DeviceVctActions.abandonVctModuleSuccess({ message: this.translate.instant('VCT_ABANDON_UNIT_SUCCESS') });
                        }
                        return DeviceVctActions.abandonVctModuleSuccess({ message: this.translate.instant('REVOKE_UNIT_SUCCESS') });
                    }
                    return DeviceVctActions.abandonVctModuleFailed({ errorMessage: this.translate.instant('ERROR') });
                }),
                catchError(error => of(DeviceVctActions.abandonVctModuleFailed({ errorMessage: error.message })))
            )
        )
    ));

    abandonVctFeature$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceVctActions.abandonVctFeature),
        switchMap((action) => this.deviceVctService.abandonVctFeature(action.featureId)
            .pipe(
                map(resp => {
                    if (resp !== undefined) {
                        if (action.abandon) {
                            return DeviceVctActions.abandonVctFeatureSuccess({ message: this.translate.instant('VCT_ABANDON_FEATURE_SUCCESS') });
                        }
                        return DeviceVctActions.abandonVctFeatureSuccess({ message: this.translate.instant('REVOKE_FEATURE_SUCCESS') });
                    }
                    return DeviceVctActions.abandonVctFeatureFailed({ errorMessage: this.translate.instant('ERROR') });
                }),
                catchError(error => of(DeviceVctActions.abandonVctFeatureFailed({ errorMessage: error.message })))
            )
        )
    ));



    massiveVctInvitations$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceVctActions.massiveVctInvitations),
        switchMap((action) => this.deviceVctService.massiveVctInvitations(action.plantId, action.plant, true)
            .pipe(
                map(resp => {
                    if (resp !== undefined) {
                        return DeviceVctActions.massiveVctInvitationsSuccess({ message: this.translate.instant('VCT_MASSIVE_INVITATION_SUCCESS') });
                    }
                    return DeviceVctActions.massiveVctInvitationsFailed({ errorMessage: this.translate.instant('ERROR') });
                }),
                catchError(error => of(DeviceVctActions.massiveVctInvitationsFailed({ errorMessage: error.message })))
            )
        )
    ));


    notifySuccess$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceVctActions.createVctInvitationsSuccess,
            DeviceVctActions.enableVctAuxSuccess,
            DeviceVctActions.enableVctAuxSuccess,
            DeviceVctActions.editVctAuxSuccess,
            DeviceVctActions.disableVctAuxSuccess,
            DeviceVctActions.editVctFeatureNameSuccess,
            DeviceVctActions.editVctModuleNameSuccess,
            DeviceVctActions.resetVctInvitationAppSuccess,
            DeviceVctActions.removeVctInvitationsSuccess,
            DeviceVctActions.abandonVctPlantSuccess,
            DeviceVctActions.abandonVctModuleSuccess,
            DeviceVctActions.massiveVctInvitationsSuccess,
            DeviceVctActions.abandonVctFeatureSuccess,

        ),
        tap((action) => {
            const notification = new Notification("DEVICE", action.message, NotificationLevel.Success, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });

    notifyError$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceVctActions.getDevicesVctListFailed,
            DeviceVctActions.getVctInvitationsFailed,
            DeviceVctActions.createVctInvitationsFailed,
            DeviceVctActions.enableVctAuxFailed,
            DeviceVctActions.editVctAuxFailed,
            DeviceVctActions.disableVctAuxFailed,
            DeviceVctActions.editVctFeatureNameFailed,
            DeviceVctActions.editVctModuleNameFailed,
            DeviceVctActions.resetVctInvitationAppFailed,
            DeviceVctActions.removeVctInvitationsFailed,
            DeviceVctActions.abandonVctPlantFailed,
            DeviceVctActions.abandonVctModuleFailed,
            DeviceVctActions.massiveVctInvitationsFailed,
        ),
        tap((action) => {
            if (action.errorMessage === '') {
                return;
            }
            const notification = new Notification("DEVICE-VCT", action.errorMessage, NotificationLevel.Error, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });

    constructor(
        private actions$: Actions,
        private deviceVctService: DeviceVctService,
        private translate: TranslateService,
        private store: Store<AppState>,
    ) { }
}

